import type React from 'react'
import { twJoin } from 'tailwind-merge'
import type { SourcesId } from '@/components/regionen/pageRegionSlug/mapData/mapDataSources/sources.const'
import { NodataFallback } from './compositTableRows/NodataFallback'
import {
  tagsTableLabelCellClass,
  tagsTableLabelContentClass,
  tagsTableRowClass,
  tagsTableValueCellClass,
} from './tagsTableLayout'
import { TagsTableRowValueWithTooltip } from './TagsTableRowValueWithTooltip'
import { ConditionalFormattedKey } from './translations/ConditionalFormattedKey'
import { splitSemicolonRespectingBrackets } from './utils/splitSemicolonRespectingBrackets'

type TagsTableRowTone = 'primary' | 'secondary'
type TagsTableRowFrameProps = {
  label: React.ReactNode
  tone?: TagsTableRowTone
  children: React.ReactNode
}

export type TagsTableRowProps =
  | {
      sourceId: SourcesId | string // string = StaticDatasetsIds
      tagKey: string
      /** @desc `null` renders <NodataFallback> */
      tagValue: string | null
      children?: never
    }
  | {
      sourceId: SourcesId | string // string = StaticDatasetsIds
      tagKey: string
      tagValue?: never
      children: React.ReactNode
    }

export const TagsTableRowFrame = ({
  label,
  tone = 'primary',
  children,
}: TagsTableRowFrameProps) => {
  const isSecondaryRow = tone === 'secondary'

  return (
    <tr className={tagsTableRowClass}>
      <td
        className={twJoin(
          tagsTableLabelCellClass,
          isSecondaryRow ? 'text-gray-400 group-hover:text-gray-900' : 'text-gray-900',
        )}
      >
        <span className={tagsTableLabelContentClass}>{label}</span>
      </td>
      <td
        className={twJoin(
          tagsTableValueCellClass,
          isSecondaryRow ? 'text-gray-400 group-hover:text-gray-500' : 'text-gray-500',
        )}
      >
        {children}
      </td>
    </tr>
  )
}

export const TagsTableRow = ({ sourceId, tagKey, tagValue, children }: TagsTableRowProps) => {
  const secondaryRowPrefixes = ['tilda_', 'prio_', 'value_']
  const isSecondaryRow = secondaryRowPrefixes.some((prefix) => tagKey.startsWith(prefix))

  return (
    <TagsTableRowFrame
      label={<ConditionalFormattedKey sourceId={sourceId} tagKey={tagKey} />}
      tone={isSecondaryRow ? 'secondary' : 'primary'}
    >
      {tagValue === null && <NodataFallback />}
      {tagValue === undefined && !children && <NodataFallback />}
      {tagValue && (
        <TagsTableRowMaybeList sourceId={sourceId} tagKey={tagKey} tagValue={tagValue} />
      )}
      {children && <>{children}</>}
    </TagsTableRowFrame>
  )
}

// Some tags are in fact lists of values, eg. `parking.condition_category`. We translate those only once and list them as list.
const TagsTableRowMaybeList = ({
  sourceId,
  tagKey,
  tagValue,
}: Pick<TagsTableRowProps, 'sourceId' | 'tagKey' | 'tagValue'>) => {
  if (!tagValue) return null
  // List of tags that should never be considered lists
  const disallowList = ['description', 'note']
  if (disallowList.includes(tagKey) || typeof tagValue !== 'string') {
    return <TagsTableRowValueWithTooltip sourceId={sourceId} tagKey={tagKey} tagValue={tagValue} />
  }

  const listValues = splitSemicolonRespectingBrackets(tagValue)
  if (listValues.length === 1) {
    return <TagsTableRowValueWithTooltip sourceId={sourceId} tagKey={tagKey} tagValue={tagValue} />
  }

  return (
    <ul className="list-disc pl-3">
      {listValues.map((value) => {
        return (
          <li key={value}>
            <TagsTableRowValueWithTooltip sourceId={sourceId} tagKey={tagKey} tagValue={value} />
          </li>
        )
      })}
    </ul>
  )
}
